/**
 * SecurityEvents.tsx — Security event monitoring for super_admin.
 *
 * Shows: failed logins, blocked accounts, password resets, suspicious IPs,
 * active sessions with per-session kill button.
 */

import React, { useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import BlockIcon from '@mui/icons-material/Block';
import KeyIcon from '@mui/icons-material/Key';
import LogoutIcon from '@mui/icons-material/Logout';
import SecurityIcon from '@mui/icons-material/Security';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WifiOffIcon from '@mui/icons-material/WifiOff';

import axios from 'axios';
import API_BASE from '../../config/api';

interface SecurityData {
  failed_logins_24h: LogEntry[] | number;
  blocked_accounts: LogEntry[] | number;
  password_resets: LogEntry[] | number;
  suspicious_ips: { ip: string; attempts: number; last_seen?: string }[];
}

interface Session {
  id: string;
  user_id: string;
  user_email?: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
  expires_at: string;
}

interface LogEntry {
  id: string;
  action: string;
  ip_address: string;
  success: boolean;
  created_at: string;
  details?: Record<string, unknown>;
}

function MetricCard({
  icon,
  label,
  value,
  color = 'primary.main',
  alert = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color?: string;
  alert?: boolean;
}) {
  return (
    <Card
      sx={{
        borderRadius: 2,
        border: alert && value > 0 ? '1px solid rgba(239,35,60,0.4)' : '1px solid rgba(255,255,255,0.06)',
        bgcolor: alert && value > 0 ? 'rgba(239,35,60,0.06)' : 'background.paper',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box sx={{ color }}>{icon}</Box>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
        <Typography variant="h4" fontWeight={700} sx={{ color: alert && value > 0 ? 'error.main' : 'text.primary' }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function SecurityEvents() {
  const [security, setSecurity] = useState<SecurityData | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [logs, setLogs]         = useState<LogEntry[]>([]);
  const [loading, setLoading]   = useState(true);
  const [msg, setMsg]           = useState('');

  async function fetchAll() {
    setLoading(true);
    try {
      const [secRes, sessRes, logRes] = await Promise.all([
        axios.get(`${API_BASE}/admin/security-events`),
        axios.get(`${API_BASE}/admin/sessions`),
        axios.get(`${API_BASE}/admin/logs?limit=50&action=login_failed`),
      ]);
      setSecurity(secRes.data);
      setSessions(sessRes.data.sessions ?? sessRes.data);
      // /admin/logs returns a bare array
      setLogs(Array.isArray(logRes.data) ? logRes.data : (logRes.data.logs ?? []));
    } catch { /* ignore */ }
    setLoading(false);
  }

  useEffect(() => { fetchAll(); }, []);

  async function killSession(id: string) {
    try {
      await axios.delete(`${API_BASE}/admin/sessions/${id}`);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      setMsg('Session terminated.');
    } catch { setMsg('Failed to terminate session.'); }
  }

  if (loading) return <Box p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SecurityIcon color="primary" /> Security Events
      </Typography>

      {msg && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      {/* Metric cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<WarningAmberIcon />}
            label="Failed Logins (24h)"
            value={Array.isArray(security?.failed_logins_24h) ? security!.failed_logins_24h.length : (security?.failed_logins_24h ?? 0) as number}
            color="error.main"
            alert
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<BlockIcon />}
            label="Blocked Accounts"
            value={Array.isArray(security?.blocked_accounts) ? security!.blocked_accounts.length : (security?.blocked_accounts ?? 0) as number}
            color="warning.main"
            alert
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<KeyIcon />}
            label="Password Resets (24h)"
            value={Array.isArray(security?.password_resets) ? security!.password_resets.length : (security?.password_resets ?? 0) as number}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<WifiOffIcon />}
            label="Suspicious IPs"
            value={security?.suspicious_ips?.length ?? 0}
            color="error.main"
            alert
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Suspicious IPs */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Suspicious IPs (≥5 failed attempts)
            </Typography>
            {!security?.suspicious_ips?.length ? (
              <Typography color="text.secondary" variant="body2">No suspicious IPs detected.</Typography>
            ) : (
              <Stack spacing={1}>
                {security.suspicious_ips.map((ip) => (
                  <Box
                    key={ip.ip}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: 'rgba(239,35,60,0.07)',
                      border: '1px solid rgba(239,35,60,0.2)',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{ip.ip}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={`${ip.attempts} attempts`} size="small" color="error" />
                      {ip.last_seen && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(ip.last_seen).toLocaleTimeString()}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Failed login log */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>Recent Failed Logins</Typography>
            {logs.length === 0 ? (
              <Typography color="text.secondary" variant="body2">No failed logins recorded.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>IP Address</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.slice(0, 15).map((l) => (
                      <TableRow key={l.id}>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {l.ip_address || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(l.created_at).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {l.details?.email ? String(l.details.email) : l.action}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Active sessions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Active Sessions ({sessions.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {sessions.length === 0 ? (
              <Typography color="text.secondary" variant="body2">No active sessions.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>IP Address</TableCell>
                      <TableCell>Device</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell align="right">Kill</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessions.map((s) => (
                      <TableRow key={s.id} hover>
                        <TableCell>
                          <Typography variant="caption">{s.user_email ?? s.user_id}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{s.ip_address}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 220, display: 'block' }}>
                            {s.user_agent?.slice(0, 60) ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(s.created_at).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(s.expires_at).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="error"
                            startIcon={<LogoutIcon fontSize="small" />}
                            onClick={() => killSession(s.id)}
                          >
                            Kill
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
